import React, { useEffect, useState } from "react";
import SettingsEmptyState from '../../components/settings/SettingsEmptyState';
import {
  ConfirmDelete,
  MwInputText,
  MwSpinnerButton,
  NewButton,
  NewInputLang,
  MwSelectorAccount,
} from "../../components/ui";
import { IoMdAdd } from "react-icons/io";
import { BiEdit } from "react-icons/bi";
import { RiDeleteBinLine } from "react-icons/ri";
import { useTranslation } from "react-i18next";
import { useCurrent, useDelete, useFetch, usePost, usePut } from "../../hooks";

const BankAccountsSettings = ({ apiBaseUrl }) => {
  const { t } = useTranslation("settings");
  const [langar, setLangar] = useState(true);
  const [accountList, setAccountList] = useState([]);
  const [accountCode, setAccountCode] = useState("");
  const [accountName, setAccountName] = useState("");
  const [accountEnName, setAccountEnName] = useState("");
  const [subAccount, setSubAccount] = useState("");
  const [bankAccountNo, setBankAccountNo] = useState("");
  const [bankAccountIban, setBankAccountIban] = useState("");
  const [error, setError] = useState({});
  const [onShow, setOnShow] = useState(false);
  const [selectedAccountId, setSelectedAccountId] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editingAccountId, setEditingAccountId] = useState(null);
  const { currentTenantId, currentCompanyId, currentBranchId, currentLangId } =
    useCurrent();
  const { postData, loading } = usePost();
  const { loading: delloading, deleteItem } = useDelete();
  const canFetchContext = Boolean(
    currentTenantId && currentCompanyId && currentBranchId
  );
  const baseFinanceUrl = (apiBaseUrl || process.env.REACT_APP_FINANCE_API_SERVER_BASE_URL || "").replace(/\/+$/, "");
  const buildFinanceUrl = (path) =>
    `${baseFinanceUrl}/${String(path || "").replace(/^\/+/, "")}`;
  const {
    data: accountsResult,
    loading: loadingAccounts,
    refreshHandler: refetchAccounts,
  } = useFetch(
    canFetchContext && baseFinanceUrl
      ? buildFinanceUrl(
          `banks-accounts/${currentTenantId}/${currentCompanyId}/${currentBranchId}`
        )
      : null
  );
  const { data: accountsCountResult, refreshHandler } = useFetch(
    canFetchContext && baseFinanceUrl
      ? buildFinanceUrl(
          `banks-accounts/count/${currentTenantId}/${currentCompanyId}/${currentBranchId}`
        )
      : null
  );
  const { loading: saveloading, putData } = usePut();

  useEffect(() => {
    if (accountsCountResult && !isEditing) {
      setAccountCode((parseInt(accountsCountResult?.data) + 1).toString());
    }
  }, [accountsCountResult, isEditing]);

  useEffect(() => {
    if (accountsResult?.data) {
      setAccountList(accountsResult.data);
    }
  }, [accountsResult]);

  const addAccount = () => {
    let errors = {};

    if (!accountCode) errors.code = "Code Is Required";
    if (!bankAccountIban) errors.bank_account_iban = "bank_account_iban Is Required";
    if (!accountName) errors.name_ar = "Arabic Name Is Required";

    if (Object.keys(errors).length > 0) {
      if (errors.name_ar) setLangar(true);
      setError(errors);
      return;
    }

    const accountData = {
      code: accountCode,
      name_ar: accountName || "-",
      name_en: accountEnName || "-",
      account_no: subAccount,
      bank_account_no: bankAccountNo,
      bank_account_iban: bankAccountIban,
      tenant_id: currentTenantId,
      company_id: currentCompanyId,
      branch_id: currentBranchId,
    };

    const resetForm = () => {
      setAccountCode("");
      setAccountName("");
      setAccountEnName("");
      setBankAccountIban("");
      setBankAccountNo("");
      setSubAccount("");
      setIsEditing(false);
      setEditingAccountId(null);
      setError({});
    };

    if (!baseFinanceUrl) return;
    if (!isEditing) {
      postData(
        buildFinanceUrl("banks-accounts"),
        accountData
      ).then(() => {
        refetchAccounts();
        refreshHandler();
        resetForm();
      });
    } else {
      putData(
        buildFinanceUrl(`banks-accounts/${editingAccountId}`),
        accountData
      ).then(() => {
        refetchAccounts();
        refreshHandler();
        resetForm();
      });
    }
  };

  const deleteAccount = (id) => {
    if (!baseFinanceUrl) return;
    deleteItem(buildFinanceUrl(`banks-accounts/${id}`)).then(() => {
      refetchAccounts();
      refreshHandler();
      setOnShow(false);
    });
  };

  return (
    <>
      {!accountCode ? (
        <div className="h-20 w-full justify-center items-center flex-wrap flex">
          <MwSpinnerButton />
        </div>
      ) : (
        <section className="flex flex-col flex-1">
          <ConfirmDelete
            onShow={onShow}
            setOnShow={setOnShow}
            onDeleteHandler={() => deleteAccount(selectedAccountId)}
            loading={delloading}
            msg={t("account.accountydel")}
          />
          <div className="flex justify-start  flex-wrap w-full items-end gap-2">
            <div className="w-24">
              <MwInputText
                invalid={error?.code}
                disabled={true}
                label={t("account.accountcode")}
                value={accountCode}
                onChange={(e) => setAccountCode(e.target.value)}
                classNameInput="w-[100%] text-center  flex-1  "
              />
            </div>
            <div className="w-48">
              <MwInputText
                invalid={error?.bank_account_no}
                disabled={false}
                label={t("account.accountno")}
                placeholder={t("account.accountnoplace")}
                value={bankAccountNo}
                onChange={(e) => setBankAccountNo(e.target.value)}
                classNameInput="w-[100%]  flex-1  "
                onInput={(e) => {
                  e.target.value = e.target.value.replace(/\D/g, "");
                }}
              />
            </div>
            <div className="w-48">
              <MwInputText
                invalid={error?.bank_account_iban}
                disabled={false}
                label={t("account.ibanaccount")}
                placeholder={t("account.ibanaccountplace")}
                value={bankAccountIban}
                onChange={(e) => setBankAccountIban(e.target.value)}
                classNameInput="w-[100%]  flex-1  "
                onInput={(e) => {
                  e.target.value = e.target.value
                    .replace(/[^0-9a-zA-Z]/g, "")
                    .toUpperCase();
                }}
              />
            </div>
            <div className="w-48 flex flex-col justify-center">
              <NewInputLang
                invalid={error?.name_ar || error?.name_en}
                placeholder={t("account.accountnameplaceholder")}
                label={t("account.accountname")}
                value={langar ? accountName : accountEnName}
                langar={langar}
                onChange={(value) => {
                  if (langar) {
                    setAccountName(value);
                  } else {
                    setAccountEnName(value);
                  }
                }}
                onChangeLang={() => setLangar(!langar)}
                classNameInput="w-[100%] flex-1"
              />
            </div>
            <div className="w-48">
              <MwSelectorAccount
                withLabel={true}
                placeholder={t("account.accountAccount")}
                classNamePrivate="flex-1 w-full"
                invalid={error?.account}
                initialValue={subAccount?.length > 0 ? subAccount : ""}
                onChangeCallback={(account) =>
                  setSubAccount(account.name_ar + "/" + account.account_no || "")
                }
              />
            </div>
            <div className="group">
              <NewButton onClick={addAccount}>
                {loading || saveloading ? (
                  <MwSpinnerButton />
                ) : isEditing ? (
                  t("general.update")
                ) : (
                  <>
                    <IoMdAdd className="text-white text-lg group-hover:text-[#fffff]" />
                    <p className="">{t("general.Add")}</p>
                  </>
                )}
              </NewButton>
            </div>
          </div>

          {loadingAccounts && accountList?.length === 0 ? (
            <div className="h-40 flex justify-center items-center w-full">
              <MwSpinnerButton />
            </div>
          ) : accountList?.length > 0 ? (
            <>
              <>
                <div className="flex w-full mt-3 justify-around bg-[#F2F0FC] p-3 rounded-t-lg font-[800] text-[#251658] text-sm">
                  <div className="w-0 text-center">#</div>
                  <div className="w-40 text-center">
                    {t("account.accountcode")}
                  </div>
                  <div className="w-40 text-center">
                    {t("account.accountno")}
                  </div>
                  <div className="w-40 text-center">
                    {t("account.ibanaccount")}
                  </div>
                  <div className="w-40 text-center">
                    {t("account.accountname")}
                  </div>
                  <div className="w-40 text-center">
                    {t("account.accountaccountshow")}
                  </div>
                  <div className="flex-1 text-center"></div>
                </div>
                <div className="divide-y  ">
                  {accountList.map((item, index) => (
                    <div
                      key={index}
                      className="flex w-full border-b border-[#ddd0bf] border-opacity-25 cursor-pointer justify-around items-center p-3 text-[#251658] font-semibold text-sm"
                    >
                      <div className="w-1 text-center">{index + 1}</div>
                      <div className="w-40 text-center">{item?.code}</div>
                      <div className="w-40 text-center">
                        {item?.bank_account_no}
                      </div>
                      <div className="w-40 text-center">
                        {item?.bank_account_iban}
                      </div>
                      <div className="w-40 text-center">
                        {currentLangId === "ar" ? item?.name_ar : item?.name_en}
                      </div>
                      <div className="w-40 text-center">
                        {item?.account_no || item?.account}
                      </div>
                      <div className=" flex me-4 justify-end items-center flex-1 text-end gap-1">
                        <button
                          onClick={() => {
                            setIsEditing(true);
                            setEditingAccountId(item.id);
                            setAccountCode(item.code);
                            setAccountName(item.name_ar);
                            setAccountEnName(item.name_en);
                            setBankAccountIban(item.bank_account_iban);
                            setBankAccountNo(item.bank_account_no);
                            setSubAccount(
                              item.account_no ||
                                (item.account
                                  ? item.account.name_ar +
                                    "/" +
                                    item.account.account_no
                                  : "")
                            );
                          }}
                          className="text-blue-500 hover:text-blue-700 mx-2"
                        >
                          <BiEdit size={16} />
                        </button>
                        <button
                          onClick={() => {
                            setSelectedAccountId(item.id);
                            setOnShow(true);
                          }}
                          className="text-red-500 hover:text-red-700"
                        >
                          <RiDeleteBinLine size={16} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            </>
          ) : (
            <div className=" h-[calc(100vh-230px)] ">
              <SettingsEmptyState msg={t("account.validate")} />
            </div>
          )}
        </section>
      )}
    </>
  );
};

export default BankAccountsSettings;
